import { yup } from '@framework/validation';
import { isStaticActive } from '../../inputs';
import { getPagesInRoot } from '../../layout/pages/getPagesInRoot';
import { IEngineSection } from '../../schema/sections/IEngineSection';
import {
  IParentWidgetScope,
  IParentWidgetScopeItem,
} from '../../schema/widgets/IWidgetType';
import { Widget } from '../../schema/widgets/Widget';
import { WidgetRepository } from '../../schema/widgets/repository/WidgetRepository';
import { createSectionsStatusSchema } from '../status/SectionStatusSchema';
import { mergeList } from '../utils/mergeList';
import {
  isCiclicDependencyError,
  manageCiclicDependencyError,
} from './manageCiclicDependacyError';
import { setStepValidation } from './setValidationStep';
import { setWidgetTypeMeta } from './setWidgetTypeMeta';

export class FormValidatorBuilder {
  /**
   * Fornita una lista di sezioni, genera il validatore per il
   * corrispetivo form.
   */
  static build(sections: IEngineSection[]) {
    try {
      return {
        schema: yup
          .object()
          .shape(mergeList(this.createSectionsSchemaShape(sections)))
          .required()
          .noUnknown(),
      };
    } catch (e: any) {
      if (isCiclicDependencyError(e)) {
        return manageCiclicDependencyError(e);
      }

      throw e;
    }
  }

  static buildSection(section: IEngineSection) {
    return this.buildSections([section]);
  }

  /**
   * Fornita una lista di sezioni, genera il validatore corrispondente.
   *
   * @param rootScope Oggetto con le proprietà da aggiungere allo scope in tutti i casi, ad esempio per gestire il readonly
   */
  static buildSections(
    sections: IEngineSection[],
    rootScope: Partial<IParentWidgetScopeItem> = {},
  ) {
    try {
      const shape = mergeList([
        createSectionsStatusSchema(sections),
        ...sections.map(section => ({
          [section.id]: yup
            .object(
              mergeList(this.createSectionsSchemaShape([section], rootScope)),
            )
            .meta({ sectionId: section.id })
            .addChildrenParentDeps(),
        })),
      ]);

      return {
        schema: yup.object().shape(shape).required().noUnknown(),
      };
    } catch (e: any) {
      if (isCiclicDependencyError(e)) {
        return manageCiclicDependencyError(e);
      }

      throw e;
    }
  }

  /**
   * Restituisce una shape (oggetto { chiave: schema } da utilizzare in `yup.object().shape()`)
   * di una specifica sezione.
   */
  static createSectionsSchemaShape(
    sections: IEngineSection[],
    rootScope: Partial<IParentWidgetScopeItem> = {},
  ) {
    // Costruisco la struttura "a pagine" delle sezioni così da poter sapere
    // in quale step ogni widget è contenuto. (utile per la validazione parziale)
    const sectionPages = sections.map((section, index) => ({
      section,
      pages: getPagesInRoot(section.ultimaVersione?.elementi, index),
    }));

    // Costruisco la shape passando lo step di ogni pagina di widget (in maniera incrementale
    // come nel navigator)
    const schemaShape = sectionPages.flatMap(({ section, pages }) =>
      pages.map(page => {
        const result = this.createWidgetsSchemaShape(
          page.widgets,
          page.sectionIndex,
          page.pageIndex,
          this.buildSectionParentScope(section, rootScope),
        );
        delete result.__isWidgetSchemaShape;
        return result;
      }),
    );

    return schemaShape;
  }

  /**
   * Costruisce lo scope parent per la sezione fornita, in modo che la
   * validazione possa essere disabilitata o resa non visibile.
   *
   * @param partialRootScope Oggetto con le proprietà da aggiungere allo scope in tutti i casi
   */
  private static buildSectionParentScope(
    section: IEngineSection,
    partialRootScope: Partial<IParentWidgetScopeItem> = {},
  ) {
    if (
      isStaticActive(section.ultimaVersione?.visibile) &&
      isStaticActive(section.ultimaVersione?.compilabile)
    ) {
      return [
        {
          visible: undefined,
          enabled: undefined,
          code: section.id,
          ...section.sectionRootScope,
          // Lo scope passato a livello di creazione del validatore è quello
          // più forte, quindi sovrascrive le proprietà della sezione
          ...partialRootScope,
        },
      ];
    }

    return [
      {
        visible: section.ultimaVersione?.visibile,
        enabled: section.ultimaVersione?.compilabile,
        code: section.id,
        ...section.sectionRootScope,
        ...partialRootScope,
      },
    ];
  }

  /**
   * Data una lista di widget (e.g. `elementi` di una sezione o `children` di un container)
   * restituisce una shape (oggetto { chiave: schema } da utilizzare in `yup.object().shape()`)
   * del validatore corrispondente.
   */
  static createWidgetsSchemaShape(
    widgets: Widget[],
    section: number,
    page: number,
    parentScope: IParentWidgetScope = [],
  ) {
    if (!widgets) return {};

    const result = mergeList(
      widgets?.map((widget: Widget) => {
        return this.createWidgetSchemaShape(widget, section, page, parentScope);
      }) ?? [],
    );

    return { ...result, __isWidgetSchemaShape: true };
  }

  /**
   * Restituisce una shape (oggetto { chiave: schema } da utilizzare in `yup.object().shape()`)
   * del validatore del widget fornito.
   */
  static createWidgetSchemaShape(
    widget: Widget,
    section: number,
    page: number,
    parentScope: IParentWidgetScope,
  ) {
    const widgetType = WidgetRepository.find(widget.type);
    if (!widgetType?.createFormSchema) {
      return {};
    }
    const widgetSchema = widgetType.createFormSchema({
      widget,
      parentScope,
      section,
      page,
    });

    if (widgetSchema.__isWidgetSchemaShape) {
      delete widgetSchema.__isWidgetSchemaShape;
      return widgetSchema;
    }

    // Imposta la validazione per evitare di validare step (sezioni/pagine) ancora non visualizzate
    // Utilizzo Object.keys perché lo schema ritorna nella forma { [widget-code]: schema }, ma in realtà gira 1 sola volta
    Object.keys(widgetSchema).forEach(key => {
      widgetSchema[key] = setStepValidation(
        widgetSchema[key],
        widget,
        widgetType,
        {
          widgetSectionStep: section,
          widgetPageStep: page,
          parentScope,
        },
      );

      widgetSchema[key] = setWidgetTypeMeta(widgetSchema[key], widget);

      // Accumulo i meta extra forniti da tutti i parent per impostarli nel widget
      const parentScopeMeta = parentScope.reduce(
        (acc, scope) => ({
          ...acc,
          ...(scope.extraMeta ?? {}),
        }),
        {},
      );
      widgetSchema[key] = widgetSchema[key].meta(parentScopeMeta);
    });

    return widgetSchema;
  }
}
